/**
 * The Acknowledgements tab in Settings (the route and identifiers keep `attributions`): the projects AIOE is built on (credits.ts), then every
 * package and module that ships in it (attributions.ts, generated from what is installed by
 * scripts/gen-attributions.ts). The same page the workbench has, in the console's own pieces.
 */
import { Segment } from '@heroui-pro/react';
import { useMemo, useState } from 'react';

import { ATTRIBUTIONS } from '../attributions.ts';
import { CREDITS } from '../credits.ts';
import { productName } from '../edition.ts';
import { inputClass, ListBlock, ListRow, PlainPanel } from './ui.tsx';

/** Packages whose terms differ in kind, not only in wording. */
const NOTABLE: Record<string, string> = {
  '@heroui-pro/react': 'Commercial licence, not open source. Used under Mojo Up’s licence.',
  'golang.zx2c4.com/wireguard': 'wireguard-go. “WireGuard” is a registered trademark of Jason A. Donenfeld.',
};

const PARTS = ['All', 'Platform API', 'Console', 'Phone app', 'Mesh agent'] as const;
type Part = (typeof PARTS)[number];

/** A licence as a person reads it: npm's "SEE LICENSE IN …" means the package carries its own terms. */
const licenceText = (l: string) => (/^SEE LICEN[SC]E/i.test(l) ? 'Own licence' : l);

const linkClass = 'font-medium text-foreground underline-offset-2 hover:underline';

export function AttributionsPanel() {
  const [query, setQuery] = useState('');
  const [part, setPart] = useState<Part>('All');

  const entries = useMemo(() => {
    const q = query.trim().toLowerCase();
    return ATTRIBUTIONS.filter((a) => (part === 'All' || a.roots.includes(part)) && (!q || a.name.toLowerCase().includes(q) || licenceText(a.license).toLowerCase().includes(q)));
  }, [query, part]);

  const licences = useMemo(() => {
    const counts = new Map<string, number>();
    for (const a of ATTRIBUTIONS) counts.set(licenceText(a.license), (counts.get(licenceText(a.license)) ?? 0) + 1);
    return [...counts.entries()].sort((a, b) => b[1] - a[1]);
  }, []);

  return (
    <>
      <PlainPanel id="credits" title="Projects and people" description={`Work ${productName()} is built on, follows or runs on: what a list of packages cannot show.`}>
        {/* Each credit is a row of the card itself, so the card pads it and draws the hairlines, as on every other page. */}
        {CREDITS.map((c) => (
          <ListRow
            key={c.name}
            title={
              <>
                <a href={c.url} target="_blank" rel="noreferrer" className={linkClass}>
                  {c.name}
                </a>
                {/* The licence beside the name, not at the row's end: a long copyright line there squeezes the words. */}
                {c.license ? <span className="text-xs font-normal text-muted">{` \u00b7 ${c.license}`}</span> : null}
              </>
            }
            description={c.description}
          />
        ))}
      </PlainPanel>

      <PlainPanel
        id="packages"
        title="Packages"
        description={`${productName()} ships ${ATTRIBUTIONS.length} direct open-source packages and modules, and the work behind them. Each keeps its own licence; the full text travels with each package, and the links go to the source. ${licences.map(([l, n]) => (l === 'Own licence' ? `${n} under its own licence` : `${n} under ${l}`)).join(' \u00b7 ')}.`}
      >
        <div className="flex flex-wrap items-center gap-2">
          <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Filter by name or licence" aria-label="Filter acknowledgements" className={`${inputClass} max-w-xs`} />
          <div className="-mx-1 overflow-x-auto px-1 py-1">
            <Segment aria-label={`Part of ${productName()}`} size="sm" selectedKey={part} onSelectionChange={(k) => setPart(String(k) as Part)} className="w-fit">
              {PARTS.map((p) => (
                <Segment.Item key={p} id={p}>
                  {p}
                </Segment.Item>
              ))}
            </Segment>
          </div>
        </div>
        {entries.length === 0 ? (
          <ListBlock>
            <p className="m-0 text-sm text-muted">Nothing matches that filter.</p>
          </ListBlock>
        ) : (
          entries.map((a) => (
            <ListRow
              key={a.name}
              title={
                a.url ? (
                  <a href={a.url} target="_blank" rel="noreferrer" className={linkClass}>
                    {a.name}
                  </a>
                ) : (
                  a.name
                )
              }
              description={[NOTABLE[a.name], [licenceText(a.license), ...a.roots].join(' \u00b7 ')].filter(Boolean).join(' \u00b7 ')}
              value={<span className="tabular-nums">{a.version}</span>}
            />
          ))
        )}
      </PlainPanel>
    </>
  );
}
